/**
 * ThemeCoverageHeatmap — v3.18.2
 *
 * Strategy-page widget showing which Win Themes are threaded through
 * which proposal sections, sourced from /strategy/theme-coverage.
 * Helps the Capture Manager spot over/under-represented themes.
 *
 * Layout: stat-summary tiles on top, then a list of themes ordered by
 * coverage with a count-bar + the section IDs that carry them.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  BarChart3, Zap, Ghost, AlertTriangle, CheckCircle2, FileText, Sparkles,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatTile({ icon: Icon, label, value, tone = 'slate', testId }) {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    violet: 'bg-violet-50 border-violet-200 text-violet-800',
  }[tone];
  return (
    <div
      className={cn('rounded-md border p-3 flex items-center gap-3', tones)}
      data-testid={testId}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        <div className="text-xl font-bold tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {value}
        </div>
      </div>
    </div>
  );
}

export default function ThemeCoverageHeatmap({ pursuitId }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!pursuitId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/theme-coverage`,
        { headers: getAuthHeaders() },
      );
      if (res.ok) {
        const body = await res.json();
        setData(body);
      } else {
        setData(null);
      }
    } catch (_) {
      setData(null);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (data === null) {
    return <Skeleton className="h-40 w-full" />;
  }

  const themes = data.themes || [];
  const totals = data.totals || {};
  const maxCount = Math.max(1, ...themes.map(t => t.section_count || 0));

  if (themes.length === 0) {
    return (
      <Card className="border-violet-100 shadow-sm" data-testid="theme-coverage-heatmap">
        <CardContent className="p-6">
          <div className="text-center" data-testid="theme-coverage-empty">
            <Sparkles className="w-8 h-8 text-violet-300 mx-auto mb-2" />
            <p className="text-sm text-slate-700 font-medium">
              Add Win Themes above to see coverage here.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Once themes are tagged on proposal sections (in the Proposal Workspace), this panel shows which themes are over- or under-represented across volumes.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-violet-100 shadow-sm" data-testid="theme-coverage-heatmap">
      <CardHeader className="bg-gradient-to-r from-violet-50 to-fuchsia-50 border-b border-violet-100">
        <CardTitle className="flex items-center gap-2 text-violet-900">
          <BarChart3 className="w-5 h-5" />
          Theme Coverage Heatmap
        </CardTitle>
        <p className="text-xs text-slate-600 mt-1">
          Which themes are showing up across proposal sections. Tag themes section-by-section in the Proposal Workspace.
        </p>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile
            icon={Sparkles}
            label="Themes defined"
            value={totals.total_themes ?? themes.length}
            tone="violet"
            testId="theme-coverage-tile-themes"
          />
          <StatTile
            icon={FileText}
            label="Sections tagged"
            value={totals.total_sections_with_tags ?? 0}
            tone="slate"
            testId="theme-coverage-tile-sections"
          />
          <StatTile
            icon={AlertTriangle}
            label="Untagged themes"
            value={totals.untagged_themes ?? 0}
            tone={totals.untagged_themes > 0 ? 'amber' : 'emerald'}
            testId="theme-coverage-tile-untagged"
          />
          <StatTile
            icon={CheckCircle2}
            label="Discriminator coverage"
            value={`${totals.discriminator_coverage_pct ?? 0}%`}
            tone={totals.discriminator_coverage_pct >= 50 ? 'emerald' : 'amber'}
            testId="theme-coverage-tile-discriminator"
          />
        </div>

        {/* Per-theme bars */}
        <div className="space-y-1.5" data-testid="theme-coverage-list">
          {themes.map((t) => {
            const widthPct = Math.round((t.section_count / maxCount) * 100);
            const isUntagged = t.section_count === 0;
            return (
              <div
                key={t.id}
                className="flex items-center gap-3 p-2 rounded-md border border-slate-100 hover:bg-slate-50/60 transition-colors"
                data-testid={`theme-coverage-row-${t.code}`}
              >
                <Badge
                  variant="outline"
                  className="bg-violet-100 text-violet-800 border-violet-300 font-mono flex-shrink-0"
                >
                  {t.code}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-slate-900 truncate">{t.title}</span>
                    {t.discriminator && (
                      <Zap className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                    )}
                    {t.ghost && (
                      <Ghost className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    )}
                  </div>
                  <div className="mt-1 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        isUntagged ? 'bg-slate-200' :
                        t.discriminator ? 'bg-amber-400' : 'bg-violet-400',
                      )}
                      style={{ width: `${Math.max(widthPct, isUntagged ? 0 : 6)}%` }}
                    />
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <div
                    className={cn(
                      'text-sm font-bold tabular-nums',
                      isUntagged ? 'text-slate-400' : 'text-slate-900',
                    )}
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {t.section_count}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    section{t.section_count === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Orphan sections nudge */}
        {totals.orphan_sections > 0 && (
          <div className="flex items-start gap-2 text-xs text-amber-800 p-2 bg-amber-50 border border-amber-200 rounded-md" data-testid="theme-coverage-orphans">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>{totals.orphan_sections}</strong> section{totals.orphan_sections === 1 ? '' : 's'} have a draft saved but no themes tagged. Open Proposal Workspace and use the <em>Themes Threading</em> side panel to tag.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
